import { RefreshCcwDot } from 'lucide-react'

import { cn } from '@/utils'
import type { TextOnPathConfig } from '@/lib/shapes/types/text-on-path-config'

import { FOCUS_RING_CLASS } from '../constants/focus-ring-class'

interface DirectionToggleProps {
  clockwise: boolean
  onConfigChange: (patch: Partial<TextOnPathConfig>) => void
  onRestoreFocus: () => void
}

/**
 * Direction flip (WMPCM-9530, Story 3). Ungrouped and 36px — the design's last slot
 * (node I20459:491501;19946:455308) is the pill's tallest child, which is what makes it
 * 44px overall.
 *
 * A toggle, not a dropdown: the spec draws this slot with no chevron, and there are
 * exactly two directions. The `MoveHorizontal` slot beside Offset is Start Position
 * (AC-4), not this — the two are separate controls that both happen to be about where
 * the text sits.
 */
export function DirectionToggle({
  clockwise,
  onConfigChange,
  onRestoreFocus,
}: DirectionToggleProps) {
  return (
    <button
      type="button"
      onClick={() => {
        onConfigChange({ clockwise: !clockwise })
        // The bar's textarea holds focus for typing; a click on a pill control must hand
        // it straight back, same as every other control here.
        onRestoreFocus()
      }}
      aria-pressed={!clockwise}
      aria-label={
        clockwise
          ? 'Text direction: clockwise. Switch to counter-clockwise'
          : 'Text direction: counter-clockwise. Switch to clockwise'
      }
      title={clockwise ? 'Flow counter-clockwise' : 'Flow clockwise'}
      className={cn(
        'flex h-9 w-9 flex-shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-foreground transition-colors hover:bg-accent',
        FOCUS_RING_CLASS,
        // Reversed is the non-default state, so it reads as engaged rather than as just
        // another idle icon button.
        !clockwise && 'bg-accent'
      )}
    >
      {/*
        One icon for both states, mirrored when reversed. `RefreshCcwDot` is already a
        counter-clockwise glyph, so a second icon would have meant either two glyphs that
        disagree about which way is "forward" or an icon that contradicts the state it is
        showing.
      */}
      <RefreshCcwDot className={cn('h-4 w-4', clockwise && 'scale-x-[-1]')} />
    </button>
  )
}
